import React from 'react';
import { Modal, Button, ButtonVariant, Form, Flex, FlexItem, Grid, GridItem, Text } from '@patternfly/react-core';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import MultiTypeaheadSelectInput from 'ui/components/MultiTypeaheadSelectInput';
import { Spot } from 'domain/Spot';
import { AppState } from 'store/types';
import { spotSelectors } from 'store/spot';
import { connect } from 'react-redux';
import LoadingSpinner from 'ui/components/LoadingSpinner';
import moment from 'moment';

interface StateProps {
  tenantId: number;
  spotList: Spot[];
}

interface OwnProps {
  isOpen: boolean;
  onClose: () => void;
  defaultFromDate: Date;
  defaultToDate: Date;
}

const mapStateToProps = (state: AppState, ownProps: OwnProps): StateProps & OwnProps => ({
  ...ownProps,
  tenantId: state.tenantData.currentTenantId,
  spotList: spotSelectors.getSpotList(state),
});

export function ExportScheduleModal(props: StateProps & OwnProps) {
  const { t } = useTranslation('ExportScheduleModal');
  const [fromDate, setFromDate] = React.useState<Date | null>(props.defaultFromDate);
  const [toDate, setToDate] = React.useState<Date | null>(props.defaultToDate);
  const [exportedSpots, setExportedSpots] = React.useState<Spot[]>(props.spotList);
  const [isLoading, setisLoading] = React.useState(false);

  // Work around since useEffect use shallowEquality, and the same date created at different times are not equal
  const defaultFromDateTime = props.defaultFromDate.getTime();
  const defaultToDateTime = props.defaultToDate.getTime();

  React.useEffect(() => {
    if (props.isOpen) {
      setFromDate(new Date(defaultFromDateTime));
      setToDate(new Date(defaultToDateTime));
      setExportedSpots(props.spotList);
    }
  }, [props.isOpen, defaultFromDateTime, defaultToDateTime, props.spotList]);

  // const spotSet = (exportedSpots.length > 0) ? exportedSpots.map((s) => `${s.id}`).join(',') : null;
  const spotSet = exportedSpots && exportedSpots.length > 0
    ? exportedSpots.map((s) => s.id).join(',')
    : '';

  // let exportUrl = '_blank';
  if (spotSet && toDate && fromDate) {
    // exportUrl = `${process.env.REACT_APP_BACKEND_URL}/rest/tenant/${props.tenantId}/roster/shiftRosterView/excel?`
    //                 + `startDate=${moment(fromDate).format('YYYY-MM-DD')}&`
    //                 + `endDate=${moment(toDate).format('YYYY-MM-DD')}&spotList=${spotSet}`;
  }

  const exportSchedule = async () => {
    setisLoading(true);
    if (!fromDate || !toDate || !spotSet) {
      return;
    }
    const token = localStorage.getItem('token');
    const url = `${process.env.REACT_APP_BACKEND_URL}/rest/tenant/${props.tenantId}/roster/shiftRosterView/excel?`
      + `startDate=${moment(fromDate).format('YYYY-MM-DD')}&`
      + `endDate=${moment(toDate).format('YYYY-MM-DD')}&spotList=${spotSet}`;
    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      });
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `schedule-${moment().format('YYYYMMDD')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      setisLoading(false);
      props.onClose();
    } catch (error) {
      setisLoading(false);
      props.onClose();
    }
  };
  // const exportSchedule = () => {
  //   props.onClose();
  // };

  return (
    <Modal
      title={t('exportSchedule')}
      isOpen={props.isOpen}
      onClose={props.onClose}
      actions={
        [
          <Button
            aria-label="Close Modal"
            variant={ButtonVariant.tertiary}
            key={0}
            onClick={props.onClose}
          >
            {t('close')}
          </Button>,
          // <a
          //   href={exportUrl}
          //   className="pf-c-button pf-m-primary"
          //   download
          //   onClick={exportSchedule}
          // >
          //   {t('export')}
          // </a>,
          <Button
            key="export"
            onClick={exportSchedule}
          >
            {t('export')}
          </Button>,
        ]
      }
      variant="small"
      width={750}
    >
      { isLoading ? <LoadingSpinner isLoading /> : '' }
      <Form id="modal-element" onSubmit={(e) => e.preventDefault()}>
        <Flex direction={{ default: 'column' }} spaceItems={{ default: 'spaceItemsMd' }}>
          <FlexItem>
            <Grid>
              <GridItem span={4}>
                <Text>{t('fromDate')}</Text>
              </GridItem>
              <GridItem span={8}>
                <DatePicker
                  aria-label={t('fromDate')}
                  selected={fromDate}
                  onChange={setFromDate}
                />
              </GridItem>
            </Grid>
          </FlexItem>
          <FlexItem>
            <Grid>
              <GridItem span={4}>
                <Text>{t('toDate')}</Text>
              </GridItem>
              <GridItem span={8}>
                <DatePicker
                  aria-label={t('toDate')}
                  selected={toDate}
                  onChange={setToDate}
                />
              </GridItem>
            </Grid>
          </FlexItem>
          <FlexItem>
            <Grid>
              <GridItem span={4}>
                <Text>{t('forSpots')}</Text>
              </GridItem>
              <GridItem span={8}>
                <MultiTypeaheadSelectInput
                  aria-label={t('forSpots')}
                  emptyText={t('selectSpots')}
                  value={exportedSpots}
                  options={props.spotList}
                  optionToStringMap={(spot) => spot.name}
                  onChange={(newExportedSpots) => setExportedSpots(newExportedSpots)}
                />
              </GridItem>
            </Grid>
          </FlexItem>
        </Flex>
      </Form>
    </Modal>
  );
}

export default connect(mapStateToProps)(ExportScheduleModal);
